import { useState } from "react";
import { toast } from "react-toastify";
import { BOTOES_INICIAIS } from "./estadoOS";
import {
  avancarOSApi,
  retrocederOSApi,
  suspenderOSApi,
  restaurarOSApi,
  cancelarOSApi,
  negociarOSApi,
  desnegociarOSApi,
  confirmarConselhoApi,
  gerarPixApi,
  gerarBoletoApi,
} from "./service";

/**
 * Ações de transição de status da OS que não precisam de validação prévia
 * de documentos (isso fica em useAvancarOS.js). Cada ação chama a API,
 * atualiza o loading e recarrega os dados da OS via fetchData.
 */
export function useAcoesOS({ numeroSolicitacao, info, fetchData, setLoading, setEstado }) {
  const [loadingGerarPix, setLoadingGerarPix] = useState(false);
  const [loadingGerarBoleto, setLoadingGerarBoleto] = useState(false);
  const [loadingConselho, setLoadingConselho] = useState(false);
  const [pixBase64, setPixBase64] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [valor, setValor] = useState("");

  const avancarDireto = async () => {
    setLoading(true);
    try {
      const response = await avancarOSApi(numeroSolicitacao);
      if (response.erro?.categoria?.id === "B") {
        toast.error(response.erro.mensagem);
        return;
      }
      if (response) await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const retroceder = async () => {
    setLoading(true);
    try {
      const response = await retrocederOSApi(numeroSolicitacao);
      if (!response) throw new Error("Nenhuma ação foi feita pela API");
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const suspender = async () => {
    setLoading(true);
    try {
      const response = await suspenderOSApi(numeroSolicitacao);
      if (!response) throw new Error("Nenhuma ação vinda da API");
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const restaurar = async () => {
    setLoading(true);
    try {
      const response = await restaurarOSApi(numeroSolicitacao);
      if (!response) throw new Error("Nenhuma ação foi feita pela API");
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelar = async () => {
    setLoading(true);
    try {
      const response = await cancelarOSApi(numeroSolicitacao);
      if (!response) throw new Error("Nenhuma ação foi feita pela API");
      await fetchData();
      // Trava a tela no estado "cancelada": só observação e carregar doc ficam ativos.
      setEstado((prev) => ({
        ...prev,
        botoes: { ...BOTOES_INICIAIS, isBtnEditarObservacaoOS: true, isBtnCarregadorDocOS: true },
      }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const iniciarNegociacao = async (situacaoInicialParaNegociacao) => {
    setLoading(true);
    try {
      const response = await negociarOSApi(numeroSolicitacao, situacaoInicialParaNegociacao);
      if (!response) throw new Error("Nenhum dado retornado da API");
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const desfazerNegociacao = async () => {
    setLoading(true);
    try {
      const response = await desnegociarOSApi(numeroSolicitacao, info.situacao);
      if (!response) throw new Error("Nenhum dado retornado da API");
      await fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmarConselho = async (dias, motivo) => {
    if (!dias || !motivo) {
      toast.warning("Todos os campos são obrigatórios. Por favor, preencha todos os campos!");
      return false;
    }
    setLoadingConselho(true);
    try {
      const response = await confirmarConselhoApi(numeroSolicitacao, dias, motivo);
      if (!response) throw new Error("Erro ao tentar confirmar o conselho");
      await fetchData();
      return true;
    } catch (error) {
      toast.error(error.response ? `Erro na API: ${error.response.data.message}` : "Ocorreu um erro. Tente novamente.");
      return false;
    } finally {
      setLoadingConselho(false);
    }
  };

  const gerarPix = async () => {
    setLoadingGerarPix(true);
    try {
      const pixData = await gerarPixApi(numeroSolicitacao);
      if (pixData.qrCode) {
        setPixBase64(pixData.qrCode);
        setQrCode(pixData.textoImagemQRcode);
        setValor(pixData.valor);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setLoadingGerarPix(false);
    }
  };

  const gerarBoleto = async () => {
    setLoadingGerarBoleto(true);
    try {
      const response = await gerarBoletoApi(numeroSolicitacao);
      if (response?.itauCode) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = "https://shopline.itau.com.br/shopline/shopline.aspx";
        form.target = "_blank";
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "dc";
        input.value = response.itauCode;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      }
    } finally {
      setLoadingGerarBoleto(false);
    }
  };

  return {
    avancarDireto,
    retroceder,
    suspender,
    restaurar,
    cancelar,
    iniciarNegociacao,
    desfazerNegociacao,
    confirmarConselho,
    loadingConselho,
    gerarPix,
    loadingGerarPix,
    pixBase64,
    qrCode,
    setQrCode,
    valor,
    gerarBoleto,
    loadingGerarBoleto,
  };
}
